import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuth";
import { toast } from "@/admin/Toast";

export default function LoginPage() {
  const { me, loading, login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const expired = sp.get("expired");

  if (loading) return null;
  if (me) return <Navigate to="/admin" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast("success", "Signed in");
      nav("/admin", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-mist-navy text-white flex items-center justify-center px-6 font-sans">
      <div className="w-full max-w-md bg-white text-mist-ink rounded-md p-8 shadow-2xl">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png" alt="MIST" className="h-9 mb-4" />
          <div className="text-[11px] uppercase tracking-[0.24em] text-mist-gold font-semibold">CMS Admin</div>
        </div>
        {expired && (
          <div className="mb-4 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2" role="alert">
            Your session expired. Please sign in again.
          </div>
        )}
        <form onSubmit={onSubmit} data-testid="admin-login-form" noValidate className="space-y-4">
          <div>
            <label htmlFor="email" className="text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              data-testid="admin-login-email"
              className="mt-2 w-full h-11 px-3.5 bg-mist-off border border-mist-soft rounded-md focus:outline-none focus:border-mist-gold text-sm"
            />
          </div>
          <div>
            <label htmlFor="password" className="text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="admin-login-password"
              className="mt-2 w-full h-11 px-3.5 bg-mist-off border border-mist-soft rounded-md focus:outline-none focus:border-mist-gold text-sm"
            />
          </div>
          {error && (
            <div data-testid="admin-login-error" role="alert" className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={submitting}
            className="w-full h-11 bg-mist-navy text-white text-[11px] uppercase tracking-[0.22em] font-semibold rounded-full hover:bg-mist-navy/90 disabled:opacity-60"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</span>
            ) : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
